/**
 * Builders for agent mode user_context objects.
 *
 * Each builder produces an object with the shape:
 *   { type, display_name, llm: { prompt, arguments } }
 *
 * - type:          'view' | 'composite' | 'metric'
 * - display_name:  Short label shown as a pill/chip in the conversation input
 * - llm.prompt:    Natural language string describing the context for the LLM
 * - llm.arguments: Raw data used to generate the prompt
 */

import { formatTrending } from './agent-mode-metric-context';

/**
 * Build user_context for a view (e.g. filter bar sparkle button).
 */
export function buildViewContext ({ viewType, viewId, viewName }) {
    const args = { viewType, viewId, viewName };

    return {
        type: 'view',
        display_name: viewName,
        llm: {
            prompt: `The user is on the ${viewType} page for "${viewName}".`,
            arguments: args,
        },
    };
}

/**
 * Build user_context for an individual metric.
 * When composite is true, the prompt is a concise summary line for embedding in a composite context.
 */
export function buildMetricContext ({ viewType, viewId, viewName, dateRange, appId, metricTitle, metricValue, metricTrending, composite = false }) {
    const args = { viewType, viewId, viewName, dateRange, appId, metricTitle, metricValue, metricTrending };
    const trending = formatTrending(metricTrending);
    const trendingSuffix = trending ? `, ${trending}` : '';

    const prompt = composite
        ? `- ${metricTitle}: ${metricValue}${trendingSuffix}`
        : `The user is on the ${viewType} page for "${viewName}".\nThey clicked on the "${metricTitle}" metric, which shows ${metricValue} for ${dateRange}${trendingSuffix}.`;

    return {
        type: 'metric',
        display_name: metricTitle,
        llm: {
            prompt,
            arguments: args,
        },
    };
}

/**
 * Build user_context for a composite of multiple contexts (e.g. a metric card).
 * Concatenates a description with the prompts of each child context.
 */
export function buildCompositeContext ({ displayName, description, contexts }) {
    const lines = contexts.map((c) => c.llm.prompt);

    return {
        type: 'composite',
        display_name: displayName,
        llm: {
            prompt: `${description}\n${lines.join('\n')}`,
            arguments: contexts.map((c) => c.llm.arguments),
        },
    };
}
